import { useAddCarrier } from "@babackapp/private-api-client/client/use-add-carrier";
import { useIsRetractionOnly } from "@/hooks/use-is-retraction-only";
import { useAddUpsCarrier } from "@babackapp/private-api-client/client/use-add-ups-carrier";
import { useCarriersQuery } from "@babackapp/private-api-client/client/use-carriers-query";
import { useDeleteCarrierPricing } from "@babackapp/private-api-client/client/use-delete-carrier-pricing";
import { useEditCarrierPriciings } from "@babackapp/private-api-client/client/use-edit-carrier-pricings";
import { useEditCarrierTransport } from "@babackapp/private-api-client/client/use-edit-carrier-transport";
import useRefreshShippingMethods from "@babackapp/private-api-client/client/use-refresh-shipping-methods";
import useShop from "@babackapp/private-api-client/client/use-shop";
import useUpdateShopPreferences from "@babackapp/private-api-client/client/use-update-shop-preferences";
import useUpdateRoutingLocationRules from "@babackapp/private-api-client/client/use-update-shop-routing-location-rules";
import SettingsFormContainer, {
  SettingsFormHeader,
} from "@babackapp/react-hook-forms-components/form-page";
import useTranslation from "@babackapp/translations/use-translation";
import { LockedOverlay } from "@/components/plan-mini/locked-overlay";
import { PlanUpgradeCTA } from "@/components/plan-mini/plan-upgrade-cta";
import { useParams } from "react-router-dom";
import CarriersInformations from "./carriers-informations/carriers-informations";
import { CarriersPricings } from "./carriers-informations/components/carriers-pricings";
import { ReturnFeeOverrideRules } from "./carriers-informations/components/return-fee-override-rules";
import LocationsList from "./locations/locations-list";
import { ShippingMethodsCard } from "./shipping-methods-card";
import { CarriersProvider } from "./use-carriers-context";

export default function Carriers() {
  const t = useTranslation();
  const shopId = useParams<{ shopId: string }>().shopId as string;
  const { data: shop } = useShop(shopId);
  const { data: carriers } = useCarriersQuery(shopId);
  const { mutate: addCarrier } = useAddCarrier(shopId);
  const { mutate: editCarrierTransport } = useEditCarrierTransport(shopId);
  const { mutate: editCarrierPricings } = useEditCarrierPriciings(shopId);
  const { mutate: deleteCarrierPricing } = useDeleteCarrierPricing(shopId);
  const { mutate: updateShopPreferences } = useUpdateShopPreferences(shopId);
  const { mutateAsync: updateRoutingRules } =
    useUpdateRoutingLocationRules(shopId);
  const { mutate: addUpsCarrier } = useAddUpsCarrier(shopId);

  const updateShopPreferenceCanReturnWithoutLabel = (canReturn: boolean) =>
    updateShopPreferences({
      canReturnWithoutLabel: canReturn,
    });

  const { mutateAsync: refreshShippingMethods } =
    useRefreshShippingMethods(shopId);

  const isRetractionOnly = useIsRetractionOnly();

  if (!shop) return <></>;
  return (
    <CarriersProvider
      carriers={carriers || []}
      shop={shop}
      addOneCarrierTransport={addCarrier}
      editOneCarrierTransport={editCarrierTransport}
      editCarrierPricingsTransport={editCarrierPricings}
      deleteCarrierPricing={deleteCarrierPricing}
      updateShopPreferenceCanReturnWithoutLabel={
        updateShopPreferenceCanReturnWithoutLabel
      }
      editOneCarrierRutingRule={updateRoutingRules}
      addOneUpsCarrierTransport={addUpsCarrier}
      refreshShippingMethods={refreshShippingMethods}
    >
      <SettingsFormContainer>
        <SettingsFormHeader
          title={t("settings.carriers.title")}
          description={t("settings.carriers.subtitle")}
        />

        {isRetractionOnly ? (
          <div className="flex flex-col gap-4">
            {/* Entrepôts */}
            <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
              <div className="pointer-events-none select-none p-6 space-y-4">
                <div className="space-y-1 mb-4">
                  <div className="text-sm font-semibold">
                    {t("carrier.locations.title")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("carrier.locations.description")}
                  </div>
                </div>
                {[
                  {
                    name: "Baback Logistique",
                    addr: "11 rue de l'Académie, Strasbourg",
                  },
                  {
                    name: "Baback Reconditionnement",
                    addr: "148 rue des Abeilles, Paris",
                  },
                  { name: "Charlot", addr: "66 rue Saint-Antoine, Paris" },
                ].map((loc) => (
                  <div
                    key={loc.name}
                    className="flex items-center justify-between rounded-md border bg-background px-4 py-3 text-xs"
                  >
                    <div>
                      <div className="font-medium">{loc.name}</div>
                      <div className="text-muted-foreground">{loc.addr}</div>
                    </div>
                    <div className="h-5 w-20 rounded bg-muted/60" />
                  </div>
                ))}
              </div>
              <LockedOverlay
                title={t("carrier.locations.title")}
                description="Configurez vos entrepôts de destination pour router vos retours. Disponible dans le plan Lite."
                withCta
              />
            </div>

            {/* Configuration du transport */}
            <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
              <div className="pointer-events-none select-none p-6 space-y-4">
                <div className="space-y-1 mb-2">
                  <div className="text-sm font-semibold">
                    {t("settings.shipping.title")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("settings.shipping.subtitle")}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="text-xs font-medium">
                      {t("settings.shipping.canReturnWithoutLabel")}
                    </div>
                  </div>
                  <div className="h-5 w-9 rounded-full bg-indigo-500 border" />
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t("settings.shipping.rules.title")}
                  </div>
                  {[
                    "Retour défectueux → Entrepôt A",
                    "Pays FR → Entrepôt B",
                  ].map((r) => (
                    <div
                      key={r}
                      className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-xs"
                    >
                      <span className="flex-1 font-medium">{r}</span>
                      <div className="h-4 w-8 rounded-full bg-muted border" />
                    </div>
                  ))}
                </div>
              </div>
              <LockedOverlay
                title={t("settings.shipping.title")}
                description="Configurez les règles de routage vers vos entrepôts selon la raison du retour ou le pays d'expédition."
              />
            </div>

            {/* Méthodes de livraison */}
            <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
              <div className="pointer-events-none select-none p-6 space-y-4">
                <div className="text-sm font-semibold mb-2">
                  {t("settings.config.myShippingMethod")}
                </div>
                {["Colissimo", "Chronopost", "DPD"].map((m) => (
                  <div
                    key={m}
                    className="flex items-center justify-between rounded-md border bg-background px-4 py-3 text-xs"
                  >
                    <span className="font-medium">{m}</span>
                    <div className="h-4 w-16 rounded bg-muted/60" />
                  </div>
                ))}
              </div>
              <LockedOverlay
                title={t("settings.config.myShippingMethod")}
                description="Activez les méthodes de livraison disponibles pour vos clients lors d'un retour."
              />
            </div>

            {/* Tarifs transporteurs */}
            <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
              <div className="pointer-events-none select-none p-6 space-y-4">
                <div className="text-sm font-semibold mb-2">
                  Tarifs transporteurs
                </div>
                {[
                  { carrier: "Colissimo", price: "4,90 €" },
                  { carrier: "Chronopost", price: "7,50 €" },
                ].map((row) => (
                  <div
                    key={row.carrier}
                    className="flex items-center justify-between rounded-md border bg-background px-4 py-3 text-xs"
                  >
                    <span className="font-medium">{row.carrier}</span>
                    <span className="text-muted-foreground">{row.price}</span>
                  </div>
                ))}
              </div>
              <LockedOverlay
                title="Tarifs transporteurs"
                description="Définissez les tarifs appliqués à vos clients pour chaque transporteur. Disponible dans le plan Lite."
              />
            </div>

            {/* Frais de retour */}
            <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
              <div className="pointer-events-none select-none p-6 space-y-4">
                <div className="space-y-1 mb-2">
                  <div className="text-sm font-semibold">
                    {t("settings.carriers.returnFeeOverride.title")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("settings.carriers.returnFeeOverride.description")}
                  </div>
                </div>
                {[
                  { name: "Règle globale", value: "Gratuit" },
                  { name: "Colissimo", value: "4,90 €" },
                ].map((rule) => (
                  <div
                    key={rule.name}
                    className="flex items-center justify-between rounded-md border bg-background px-3 py-2 text-xs"
                  >
                    <span className="font-medium">{rule.name}</span>
                    <span className="text-muted-foreground">{rule.value}</span>
                  </div>
                ))}
              </div>
              <LockedOverlay
                title={t("settings.carriers.returnFeeOverride.title")}
                description="Paramétrez les frais déduits du remboursement lors d'un retour, par transporteur ou globalement."
                withCta
              />
            </div>
          </div>
        ) : (
          <>
            <LocationsList />
            <CarriersInformations />
            <ShippingMethodsCard />
            <CarriersPricings />
            <ReturnFeeOverrideRules />
          </>
        )}
      </SettingsFormContainer>
    </CarriersProvider>
  );
}
